import { createContext, useContext } from "react";

import { PlaygroundCredentialMode } from "../types";

/**
 * Which credential sources the Playground may offer for a replay.
 *
 * This is app state, not an API, which is why it does not live on
 * `CommonAPI` (Carly's review on #5273): that interface is otherwise
 * entirely callbacks. It has to reach `PlaygroundPage`, which lives here
 * in `packages/common`, so a context is the seam. Same pattern as
 * `AgentViewPreferencesProvider`.
 *
 * Unlike that one, this context does NOT throw without a provider: the
 * default is the legacy API-key-only behaviour. The Dashboard, which
 * derives the modes from feature flags, provides one.
 */
const DEFAULT_MODES: readonly PlaygroundCredentialMode[] = ["api_key"];

const PlaygroundCredentialModesContext = createContext<
  readonly PlaygroundCredentialMode[] | null
>(null);

/**
 * The modes available here, defaulting to `["api_key"]`.
 *
 * The first entry is the default for a user who has never chosen one
 * (see `usePlaygroundCredentials`), so ordering is meaningful: a
 * provider should lead with the mode it wants selected by default.
 */
export const usePlaygroundCredentialModes =
  (): readonly PlaygroundCredentialMode[] =>
    useContext(PlaygroundCredentialModesContext) ?? DEFAULT_MODES;

export const PlaygroundCredentialModesProvider = ({
  modes,
  children,
}: {
  modes: readonly PlaygroundCredentialMode[];
  children: React.ReactNode;
}) => (
  <PlaygroundCredentialModesContext.Provider value={modes}>
    {children}
  </PlaygroundCredentialModesContext.Provider>
);
